import { Session } from "@supabase/supabase-js";
import { getSupabaseClient } from "../config/supabase";

// OfficeRuntime type declaration (from @types/office-runtime)
declare const OfficeRuntime: {
  storage: {
    getItem(key: string): Promise<string | null>;
    setItem(key: string, value: string): Promise<void>;
    removeItem(key: string): Promise<void>;
  };
} | undefined;

const AUTH_SESSION_KEY = "larry_supabase_session";

/**
 * Subscription data from Supabase
 */
export interface SubscriptionData {
  usage_count: number;
  usage_limit: number;
}

/**
 * Fetch subscription data for the authenticated user with timeout
 */
export async function fetchSubscriptionData(userId: string): Promise<SubscriptionData | null> {
  try {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from("subscriptions")
      .select("usage_count, usage_limit")
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching subscription data:", error);

      return null;
    }

    if (!data) {
      console.warn("No subscription data found");

      return null;
    }

    return {
      usage_count: data.usage_count,
      usage_limit: data.usage_limit,
    };
  } catch (error) {
    console.error("Error fetching subscription data:", error);
    return null;
  }
}

/**
 * Storage service for authentication data in Office Add-ins
 * Uses Office.context.roamingSettings for persistent storage
 */
export class AuthStorage {
  static async saveSession(session: Session | null): Promise<void> {
    const sessionJson = session ? JSON.stringify(session) : null;

    // Save to OfficeRuntime.storage first (primary)
    if (typeof Office !== "undefined" && Office.context && OfficeRuntime && OfficeRuntime.storage) {
      try {
        if (sessionJson) {
          await OfficeRuntime.storage.setItem(AUTH_SESSION_KEY, sessionJson);
        } else {
          await OfficeRuntime.storage.removeItem(AUTH_SESSION_KEY);
        }
      } catch (error) {
        console.error("Error saving session to OfficeRuntime.storage:", error);
        // Fall through to fallback
      }
    }

    // Fallback to Office.context.roamingSettings
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        if (sessionJson) {
          Office.context.roamingSettings.set(AUTH_SESSION_KEY, sessionJson);
        } else {
          Office.context.roamingSettings.remove(AUTH_SESSION_KEY);
        }
        await new Promise<void>((resolve, reject) => {
          Office.context.roamingSettings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              reject(new Error(result.error?.message || "Failed to save settings"));
            }
          });
        });
      } catch (error) {
        console.error("Error saving session to Office settings:", error);
        // Continue to localStorage fallback
      }
    }

    // Always save to localStorage as backup (for Supabase client access)
    this.saveToLocalStorage(session);
  }

  static async getSession(): Promise<Session | null> {
    // Try OfficeRuntime.storage first (primary)
    if (typeof Office !== "undefined" && Office.context && OfficeRuntime && OfficeRuntime.storage) {
      try {
        const sessionData = await OfficeRuntime.storage.getItem(AUTH_SESSION_KEY);
        if (sessionData && typeof sessionData === "string") {
          return JSON.parse(sessionData) as Session;
        }
      } catch (error) {
        console.error("Error reading session from OfficeRuntime.storage:", error);
        // Fall through to fallback
      }
    }

    // Fallback to Office.context.roamingSettings
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        const sessionData = Office.context.roamingSettings.get(AUTH_SESSION_KEY);
        if (sessionData && typeof sessionData === "string") {
          return JSON.parse(sessionData) as Session;
        }
      } catch (error) {
        console.error("Error reading session from Office settings:", error);
        // Fall through to localStorage
      }
    }

    // Final fallback to localStorage
    return this.getFromLocalStorage();
  }

  static async clearSession(): Promise<void> {
    // Clear from OfficeRuntime.storage first (primary)
    if (typeof Office !== "undefined" && Office.context && OfficeRuntime && OfficeRuntime.storage) {
      try {
        await OfficeRuntime.storage.removeItem(AUTH_SESSION_KEY);
      } catch (error) {
        console.error("Error clearing session from OfficeRuntime.storage:", error);
        // Continue to fallback
      }
    }

    // Fallback to Office.context.roamingSettings
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        Office.context.roamingSettings.remove(AUTH_SESSION_KEY);
        await new Promise<void>((resolve, reject) => {
          Office.context.roamingSettings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve();
            } else {
              reject(new Error(result.error?.message || "Failed to clear settings"));
            }
          });
        });
      } catch (error) {
        console.error("Error clearing session from Office settings:", error);
        // Continue to localStorage
      }
    }

    // Always clear localStorage as well
    this.clearFromLocalStorage();
  }

  private static saveToLocalStorage(session: Session | null): void {
    try {
      if (session) {
        localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
      } else {
        localStorage.removeItem(AUTH_SESSION_KEY);
      }
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }

  private static getFromLocalStorage(): Session | null {
    try {
      const sessionData = localStorage.getItem(AUTH_SESSION_KEY);
      if (sessionData) {
        return JSON.parse(sessionData) as Session;
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }
    return null;
  }

  private static clearFromLocalStorage(): void {
    try {
      localStorage.removeItem(AUTH_SESSION_KEY);
    } catch (error) {
      console.error("Error clearing from localStorage:", error);
    }
  }
}
